import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import {
  WalletCards,
  User,
  Building2,
  Briefcase,
  Plus,
  Trash2,
  Edit2,
  ChevronLeft,
  ChevronRight,
  Printer,
  Sparkles,
  Search,
  Check,
  X,
  PlusCircle,
  TrendingDown,
  TrendingUp,
  HandCoins,
  ArrowRightLeft,
  Calendar,
  Layers,
  FolderPlus,
  Tag,
  Landmark,
  Smartphone,
  Banknote,
  PiggyBank,
  CreditCard,
  ArrowLeftRight,
  ShieldCheck,
  DollarSign,
  Coins,
} from 'lucide-react';
import {
  MonthlyLedgerExpense,
  MonthlyLedgerIncome,
  MonthlyLedgerDisbursement,
  FinanceFolder,
  Account,
  AccountType,
} from '../../types';
import { StorageService } from '../../lib/storage';
import { LoanManager } from './LoanManager';
import { formatMoney } from '../../lib/i18n';
import { getLocalMonthString } from '../../lib/dateUtils';

// Default categories matching the organizational spreadsheet template
export const DEFAULT_ORG_CATEGORIES = [
  'যাতায়াত',
  'আপ্যায়ন',
  'প্রোগ্রাম',
  'ডাক ও তার',
  'অফিস',
  'HRD',
  'প্রকাশনা',
  'আসবাবপত্র',
  'মেস',
  'অন্যান্য',
];

export const DEFAULT_PERSONAL_CATEGORIES = [
  'খাবার ও বাজার',
  'যাতায়াত',
  'বাসা ভাড়া ও ইউটিলিটি',
  'শিক্ষা ও বই',
  'স্বাস্থ্য ও ওষুধ',
  'পরিবার ও উপহার',
  'বিনোদন ও শপিং',
  'দান ও সাদাকাহ',
  'অন্যান্য',
];

export const DEFAULT_BUSINESS_CATEGORIES = [
  'অফিস ভাড়া',
  'বেতন ও স্টাফ',
  'সার্ভার ও সফটওয়্যার',
  'মার্কেটিং ও বিজ্ঞাপন',
  'যাতায়াত ও ভ্রমণ',
  'আপ্যায়ন ও মিটিং',
  'স্টেশনারি ও প্রিন্ট',
  'ট্যাক্স ও লিগ্যাল',
  'অন্যান্য',
];

// Clean initial data arrays for real-time ledger entries
const INITIAL_SAMPLE_EXPENSES: MonthlyLedgerExpense[] = [];
const INITIAL_SAMPLE_INCOMES: MonthlyLedgerIncome[] = [];
const INITIAL_SAMPLE_DISBURSEMENTS: MonthlyLedgerDisbursement[] = [];

const MONTH_NAMES_BN = [
  'জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন',
  'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর'
];

export const FinanceView: React.FC = () => {
  const {
    accounts,
    addAccount,
    editAccount,
    updateAccountBalance,
    deleteAccount,
    transferMoney,
    financeFolders,
    addFinanceFolder,
    deleteFinanceFolder,
    triggerConfetti,
    language,
  } = useApp();
  const isBn = language === 'bn';

  // Top Section: 'personal' | 'organization' | 'business' | 'accounts' | 'loan'
  const [activeSection, setActiveSection] = useState<'personal' | 'organization' | 'business' | 'accounts' | 'loan'>('organization');

  // Selected Business Folder ID (if activeSection === 'business')
  const businessFolders = useMemo(() => {
    return (financeFolders || []).filter((f) => f.type === 'business');
  }, [financeFolders]);

  const [selectedBusinessId, setSelectedBusinessId] = useState<string>(() => {
    const firstBiz = (financeFolders || []).find((f) => f.type === 'business');
    return firstBiz ? firstBiz.id : 'f_business_1';
  });

  // Ensure valid selectedBusinessId
  useEffect(() => {
    if (businessFolders.length > 0 && !businessFolders.some((b) => b.id === selectedBusinessId)) {
      setSelectedBusinessId(businessFolders[0].id);
    }
  }, [businessFolders, selectedBusinessId]);

  // Current Active Ledger Folder ID
  const currentFolderId = useMemo(() => {
    if (activeSection === 'personal') return 'f_personal';
    if (activeSection === 'organization') return 'f_org_1';
    return selectedBusinessId || 'f_business_1';
  }, [activeSection, selectedBusinessId]);

  // Current Month State (Dynamic Real-time e.g. '2026-08')
  const [selectedYearMonth, setSelectedYearMonth] = useState<string>(() => {
    return getLocalMonthString();
  });

  // New Business Modal State
  const [isAddBizModalOpen, setIsAddBizModalOpen] = useState(false);
  const [newBizName, setNewBizName] = useState('');
  const [newBizDesc, setNewBizDesc] = useState('');
  const [newBizColor, setNewBizColor] = useState('#3b82f6');

  // Category State per section with localStorage persistence
  const [orgCategories, setOrgCategories] = useState<string[]>(() => {
    return StorageService.getItem('mylife_org_categories', DEFAULT_ORG_CATEGORIES);
  });
  const [personalCategories, setPersonalCategories] = useState<string[]>(() => {
    return StorageService.getItem('mylife_personal_categories', DEFAULT_PERSONAL_CATEGORIES);
  });
  const [businessCategories, setBusinessCategories] = useState<string[]>(() => {
    return StorageService.getItem('mylife_business_categories', DEFAULT_BUSINESS_CATEGORIES);
  });

  // Save categories on change
  useEffect(() => {
    StorageService.setItem('mylife_org_categories', orgCategories);
  }, [orgCategories]);
  useEffect(() => {
    StorageService.setItem('mylife_personal_categories', personalCategories);
  }, [personalCategories]);
  useEffect(() => {
    StorageService.setItem('mylife_business_categories', businessCategories);
  }, [businessCategories]);

  // Add Category Modal State
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // ACCOUNTS MANAGEMENT STATE
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [accountName, setAccountName] = useState('');
  const [accountType, setAccountType] = useState<AccountType>('mobile_banking');
  const [accountBalance, setAccountBalance] = useState<string>('0');
  const [accountColor, setAccountColor] = useState('#10b981');

  // Fund Transfer Modal State
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferFromId, setTransferFromId] = useState('');
  const [transferToId, setTransferToId] = useState('');
  const [transferAmount, setTransferAmount] = useState('');

  // Quick Balance Adjustment Modal
  const [adjustingAccount, setAdjustingAccount] = useState<Account | null>(null);
  const [adjustBalanceVal, setAdjustBalanceVal] = useState<string>('');

  // Persistent Monthly Ledger State
  const [expenses, setExpenses] = useState<MonthlyLedgerExpense[]>(() => {
    return StorageService.getItem('mylife_monthly_ledger_expenses', INITIAL_SAMPLE_EXPENSES);
  });

  const [incomes, setIncomes] = useState<MonthlyLedgerIncome[]>(() => {
    return StorageService.getItem('mylife_monthly_ledger_incomes', INITIAL_SAMPLE_INCOMES);
  });

  const [disbursements, setDisbursements] = useState<MonthlyLedgerDisbursement[]>(() => {
    return StorageService.getItem('mylife_monthly_ledger_disbursements', INITIAL_SAMPLE_DISBURSEMENTS);
  });

  // Save to Storage on change
  useEffect(() => {
    StorageService.setItem('mylife_monthly_ledger_expenses', expenses);
  }, [expenses]);

  useEffect(() => {
    StorageService.setItem('mylife_monthly_ledger_incomes', incomes);
  }, [incomes]);

  useEffect(() => {
    StorageService.setItem('mylife_monthly_ledger_disbursements', disbursements);
  }, [disbursements]);

  // Categories based on active section
  const availableCategories = useMemo(() => {
    if (activeSection === 'organization') return orgCategories;
    if (activeSection === 'personal') return personalCategories;
    return businessCategories;
  }, [activeSection, orgCategories, personalCategories, businessCategories]);

  // Current Month Expenses
  const monthExpenses = useMemo(() => {
    return expenses
      .filter((e) => e.folderId === currentFolderId && e.yearMonth === selectedYearMonth)
      .sort((a, b) => a.day - b.day || a.id.localeCompare(b.id));
  }, [expenses, currentFolderId, selectedYearMonth]);

  // Current Month Incomes
  const monthIncomes = useMemo(() => {
    return incomes.filter((i) => i.folderId === currentFolderId && i.yearMonth === selectedYearMonth);
  }, [incomes, currentFolderId, selectedYearMonth]);

  // Current Month Disbursements
  const monthDisbursements = useMemo(() => {
    return disbursements.filter((d) => d.folderId === currentFolderId && d.yearMonth === selectedYearMonth);
  }, [disbursements, currentFolderId, selectedYearMonth]);

  // Totals Calculation
  const totalExpense = useMemo(() => {
    return monthExpenses.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
  }, [monthExpenses]);

  const totalIncome = useMemo(() => {
    return monthIncomes.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
  }, [monthIncomes]);

  const totalDisbursement = useMemo(() => {
    return monthDisbursements.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
  }, [monthDisbursements]);

  const currentBalance = totalIncome - totalExpense - totalDisbursement;

  // Total Net Liquid Worth from all accounts
  const totalAccountsBalance = useMemo(() => {
    return (accounts || []).reduce((acc, a) => acc + (Number(a.balance) || 0), 0);
  }, [accounts]);

  // Breakdown by Category
  const categoryBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    availableCategories.forEach((c) => {
      map[c] = 0;
    });

    monthExpenses.forEach((e) => {
      map[e.category] = (map[e.category] || 0) + (Number(e.amount) || 0);
    });

    return map;
  }, [availableCategories, monthExpenses]);

  // Inline Expense Entry Form State
  const [formDay, setFormDay] = useState<number>(new Date().getDate());
  const [formCategory, setFormCategory] = useState<string>(availableCategories[0] || 'যাতায়াত');
  const [formDescription, setFormDescription] = useState<string>('');
  const [formAmount, setFormAmount] = useState<string>('');

  // Update default form category when section changes
  useEffect(() => {
    if (availableCategories.length > 0 && !availableCategories.includes(formCategory)) {
      setFormCategory(availableCategories[0]);
    }
  }, [availableCategories, formCategory]);

  // Quick Income Form State
  const [quickIncomeName, setQuickIncomeName] = useState('');
  const [quickIncomeAmount, setQuickIncomeAmount] = useState('');

  // Quick Disbursement Form State
  const [quickDisbName, setQuickDisbName] = useState('');
  const [quickDisbAmount, setQuickDisbAmount] = useState('');

  // Month navigation helpers
  const [currYear, currMonthNum] = selectedYearMonth.split('-').map(Number);

  const handlePrevMonth = () => {
    let y = currYear;
    let m = currMonthNum - 1;
    if (m < 1) {
      m = 12;
      y -= 1;
    }
    setSelectedYearMonth(`${y}-${String(m).padStart(2, '0')}`);
  };

  const handleNextMonth = () => {
    let y = currYear;
    let m = currMonthNum + 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    setSelectedYearMonth(`${y}-${String(m).padStart(2, '0')}`);
  };

  const monthLabel = `${MONTH_NAMES_BN[currMonthNum - 1] || 'মাস'} ${currYear}`;

  // Add Category Handler
  const handleAddCategory = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanName = newCategoryName.trim();
    if (!cleanName) return;

    if (activeSection === 'organization') {
      if (!orgCategories.includes(cleanName)) {
        setOrgCategories((prev) => [...prev, cleanName]);
      }
    } else if (activeSection === 'personal') {
      if (!personalCategories.includes(cleanName)) {
        setPersonalCategories((prev) => [...prev, cleanName]);
      }
    } else {
      if (!businessCategories.includes(cleanName)) {
        setBusinessCategories((prev) => [...prev, cleanName]);
      }
    }

    setFormCategory(cleanName);
    setIsAddCategoryModalOpen(false);
    setNewCategoryName('');
    triggerConfetti();
  };

  // Delete Category Handler
  const handleDeleteCategory = (catToDelete: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeSection === 'organization') {
      setOrgCategories((prev) => prev.filter((c) => c !== catToDelete));
    } else if (activeSection === 'personal') {
      setPersonalCategories((prev) => prev.filter((c) => c !== catToDelete));
    } else {
      setBusinessCategories((prev) => prev.filter((c) => c !== catToDelete));
    }
  };

  // Add Expense
  const handleAddExpense = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formAmount || Number(formAmount) <= 0) return;

    const newExpense: MonthlyLedgerExpense = {
      id: 'exp_' + Date.now(),
      folderId: currentFolderId,
      yearMonth: selectedYearMonth,
      day: Number(formDay) || 1,
      date: `${selectedYearMonth}-${String(formDay).padStart(2, '0')}`,
      category: formCategory,
      description: formDescription.trim() || '-',
      amount: Number(formAmount),
    };

    setExpenses((prev) => [...prev, newExpense]);
    setFormDescription('');
    setFormAmount('');
  };

  // Delete Expense
  const handleDeleteExpense = (id: string) => {
    setExpenses((prev) => prev.filter((e) => e.id !== id));
  };

  // Add Income
  const handleAddIncome = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickIncomeName.trim() || !quickIncomeAmount) return;

    const newInc: MonthlyLedgerIncome = {
      id: 'inc_' + Date.now(),
      folderId: currentFolderId,
      yearMonth: selectedYearMonth,
      sourceName: quickIncomeName.trim(),
      amount: Number(quickIncomeAmount) || 0,
    };

    setIncomes((prev) => [...prev, newInc]);
    setQuickIncomeName('');
    setQuickIncomeAmount('');
    triggerConfetti();
  };

  // Delete Income
  const handleDeleteIncome = (id: string) => {
    setIncomes((prev) => prev.filter((i) => i.id !== id));
  };

  // Add Disbursement
  const handleAddDisbursement = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickDisbName.trim()) return;

    const newDisb: MonthlyLedgerDisbursement = {
      id: 'disb_' + Date.now(),
      folderId: currentFolderId,
      yearMonth: selectedYearMonth,
      recipientName: quickDisbName.trim(),
      amount: Number(quickDisbAmount) || 0,
    };

    setDisbursements((prev) => [...prev, newDisb]);
    setQuickDisbName('');
    setQuickDisbAmount('');
  };

  // Delete Disbursement
  const handleDeleteDisbursement = (id: string) => {
    setDisbursements((prev) => prev.filter((d) => d.id !== id));
  };

  // Create New Business
  const handleCreateBusiness = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBizName.trim()) return;

    const newBizId = 'f_biz_' + Date.now();
    addFinanceFolder({
      name: newBizName.trim(),
      type: 'business',
      description: newBizDesc.trim() || 'Business Ledger',
      color: newBizColor,
      icon: 'Briefcase',
    });

    setSelectedBusinessId(newBizId);
    setIsAddBizModalOpen(false);
    setNewBizName('');
    setNewBizDesc('');
    triggerConfetti();
  };

  // Open Create Account Modal
  const handleOpenCreateAccount = () => {
    setEditingAccount(null);
    setAccountName('');
    setAccountType('mobile_banking');
    setAccountBalance('0');
    setAccountColor('#10b981');
    setIsAddAccountModalOpen(true);
  };

  // Open Edit Account Modal
  const handleOpenEditAccount = (acc: Account) => {
    setEditingAccount(acc);
    setAccountName(acc.name);
    setAccountType(acc.type);
    setAccountBalance(String(acc.balance || 0));
    setAccountColor(acc.color || '#10b981');
    setIsAddAccountModalOpen(true);
  };

  // Save Account
  const handleSaveAccount = (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountName.trim()) return;

    const iconMap: Record<AccountType, string> = {
      bank: 'Landmark',
      mobile_banking: 'Smartphone',
      cash: 'Banknote',
      savings: 'PiggyBank',
      credit_card: 'CreditCard',
    };

    if (editingAccount) {
      editAccount(editingAccount.id, {
        name: accountName.trim(),
        type: accountType,
        balance: Number(accountBalance) || 0,
        color: accountColor,
        icon: iconMap[accountType],
      });
    } else {
      addAccount({
        name: accountName.trim(),
        type: accountType,
        balance: Number(accountBalance) || 0,
        color: accountColor,
        icon: iconMap[accountType],
      });
      triggerConfetti();
    }

    setIsAddAccountModalOpen(false);
    setEditingAccount(null);
  };

  // Handle Fund Transfer
  const handleExecuteTransfer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!transferFromId || !transferToId || !transferAmount || transferFromId === transferToId) return;

    transferMoney(transferFromId, transferToId, Number(transferAmount));
    setIsTransferModalOpen(false);
    setTransferAmount('');
  };

  // Quick Balance Adjustment
  const handleSaveQuickBalance = (e: React.FormEvent) => {
    e.preventDefault();
    if (!adjustingAccount) return;

    updateAccountBalance(adjustingAccount.id, Number(adjustBalanceVal) || 0);
    setAdjustingAccount(null);
    setAdjustBalanceVal('');
    triggerConfetti();
  };

  // Title header text based on section
  const sectionTitle = useMemo(() => {
    if (activeSection === 'organization') return isBn ? 'সাংগঠনিক ব্যক্তিগত খরচ' : 'Organization Expenses';
    if (activeSection === 'personal') return isBn ? 'ব্যক্তিগত মাসিক খরচ ও হিসাব' : 'Personal Monthly Ledger';
    if (activeSection === 'business') {
      const biz = businessFolders.find((b) => b.id === selectedBusinessId);
      return biz ? `${biz.name} - ${isBn ? 'মাসিক হিসাব' : 'Monthly Ledger'}` : (isBn ? 'বিজনেস হিসাব' : 'Business Ledger');
    }
    if (activeSection === 'loan') return isBn ? 'পাবো ও পাবে (লোন হিসাব)' : 'Loans & Debts (Receivable & Payable)';
    return isBn ? 'আমার অ্যাকাউন্টস (ব্যাংক, বিকাশ, নগদ ও ওয়ালেট)' : 'My Accounts (Bank, Mobile Banking, Cash)';
  }, [activeSection, selectedBusinessId, businessFolders, isBn]);

  const getAccountTypeIcon = (type: AccountType) => {
    switch (type) {
      case 'bank':
        return <Landmark className="w-5 h-5 text-blue-500" />;
      case 'mobile_banking':
        return <Smartphone className="w-5 h-5 text-pink-500" />;
      case 'cash':
        return <Banknote className="w-5 h-5 text-emerald-500" />;
      case 'savings':
        return <PiggyBank className="w-5 h-5 text-purple-500" />;
      case 'credit_card':
        return <CreditCard className="w-5 h-5 text-amber-500" />;
      default:
        return <WalletCards className="w-5 h-5 text-teal-500" />;
    }
  };

  const getAccountTypeLabel = (type: AccountType) => {
    switch (type) {
      case 'bank':
        return isBn ? 'ব্যাংক একাউন্ট' : 'Bank Account';
      case 'mobile_banking':
        return isBn ? 'বিকাশ / নগদ / রকেট' : 'Mobile Banking (bKash/Nagad)';
      case 'cash':
        return isBn ? 'ক্যাশ ওয়ালেট' : 'Cash Wallet';
      case 'savings':
        return isBn ? 'সঞ্চয় / ডিপিএস' : 'Savings / DPS';
      case 'credit_card':
        return isBn ? 'ক্রেডিট কার্ড' : 'Credit Card';
      default:
        return isBn ? 'অ্যাকাউন্ট' : 'Account';
    }
  };

  return (
    <div className="space-y-5 animate-fade-in pb-16 w-full max-w-full min-w-0">
      {/* Top 4 Main Sections Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full max-w-full min-w-0">
        {/* Section Tabs: Personal | Organization | Business | My Accounts | Loan */}
        <div className="flex items-center bg-slate-200/80 dark:bg-slate-800/90 p-1.5 rounded-2xl border border-slate-300/80 dark:border-slate-700/80 shadow-xs overflow-x-auto no-scrollbar flex-nowrap w-full sm:w-auto gap-1">
          <button
            onClick={() => setActiveSection('personal')}
            className={`px-3 sm:px-3.5 py-2 rounded-xl text-xs sm:text-sm font-black flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap transition-all ${
              activeSection === 'personal'
                ? 'bg-white dark:bg-[#111827] text-emerald-600 dark:text-emerald-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <User className="w-4 h-4 text-emerald-500" />
            <span>{isBn ? 'Personal' : 'Personal'}</span>
          </button>

          <button
            onClick={() => setActiveSection('organization')}
            className={`px-3 sm:px-3.5 py-2 rounded-xl text-xs sm:text-sm font-black flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap transition-all ${
              activeSection === 'organization'
                ? 'bg-white dark:bg-[#111827] text-purple-600 dark:text-purple-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Building2 className="w-4 h-4 text-purple-500" />
            <span>{isBn ? 'Organization' : 'Organization'}</span>
          </button>

          <button
            onClick={() => setActiveSection('business')}
            className={`px-3 sm:px-3.5 py-2 rounded-xl text-xs sm:text-sm font-black flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap transition-all ${
              activeSection === 'business'
                ? 'bg-white dark:bg-[#111827] text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Briefcase className="w-4 h-4 text-blue-500" />
            <span>{isBn ? 'Business' : 'Business'}</span>
          </button>

          <button
            onClick={() => setActiveSection('accounts')}
            className={`px-3 sm:px-3.5 py-2 rounded-xl text-xs sm:text-sm font-black flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap transition-all ${
              activeSection === 'accounts'
                ? 'bg-white dark:bg-[#111827] text-teal-600 dark:text-teal-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <WalletCards className="w-4 h-4 text-teal-500" />
            <span>{isBn ? 'My Accounts' : 'My Accounts'}</span>
          </button>

          <button
            onClick={() => setActiveSection('loan')}
            className={`px-3 sm:px-3.5 py-2 rounded-xl text-xs sm:text-sm font-black flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap transition-all ${
              activeSection === 'loan'
                ? 'bg-white dark:bg-[#111827] text-amber-600 dark:text-amber-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <HandCoins className="w-4 h-4 text-amber-500" />
            <span>{isBn ? 'পাবো / পাবে' : 'Receivable / Payable'}</span>
          </button>
        </div>

        {/* Month Selector & Print Button (Shown when not in accounts or loan tab) */}
        {activeSection !== 'accounts' && activeSection !== 'loan' && (
          <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto">
            <div className="flex items-center justify-between bg-white dark:bg-[#111827] border border-slate-300/80 dark:border-slate-800 p-1 rounded-2xl shadow-xs flex-1 sm:flex-initial">
              <button
                onClick={handlePrevMonth}
                className="p-1.5 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                title="পূর্বের মাস"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="px-3 text-xs sm:text-sm font-black text-slate-900 dark:text-white min-w-[120px] text-center font-bangla">
                {monthLabel}
              </span>

              <button
                onClick={handleNextMonth}
                className="p-1.5 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                title="পরবর্তী মাস"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={() => window.print()}
              className="p-2.5 bg-white dark:bg-[#111827] border border-slate-300/80 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl shadow-xs transition-colors flex items-center gap-1.5 text-xs font-bold flex-shrink-0"
              title="প্রিন্ট / PDF"
            >
              <Printer className="w-4 h-4 text-slate-500" />
              <span className="hidden sm:inline">প্রিন্ট</span>
            </button>
          </div>
        )}
      </div>

      {/* Multiple Business Sub-Tabs (When Business Section is active) */}
      {activeSection === 'business' && (
        <div className="p-2.5 sm:p-3 bg-white dark:bg-[#111827] border border-blue-500/30 rounded-2xl shadow-xs flex items-center justify-between gap-3 overflow-x-auto no-scrollbar">
          <div className="flex items-center gap-2 min-w-0 flex-nowrap overflow-x-auto no-scrollbar">
            <span className="text-xs font-bold text-slate-500 flex items-center gap-1 pl-1">
              <Briefcase className="w-3.5 h-3.5 text-blue-500" />
              বিজনেস তালিকা:
            </span>

            {businessFolders.map((biz) => {
              const isSelected = selectedBusinessId === biz.id;
              return (
                <div key={biz.id} className="flex items-center gap-1">
                  <button
                    onClick={() => setSelectedBusinessId(biz.id)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 border ${
                      isSelected
                        ? 'bg-blue-500 text-white border-blue-500 shadow-xs'
                        : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-blue-500'
                    }`}
                  >
                    <span>{biz.name}</span>
                  </button>

                  {businessFolders.length > 1 && (
                    <button
                      onClick={() => deleteFinanceFolder(biz.id)}
                      className="p-1 text-slate-400 hover:text-rose-500 rounded-lg"
                      title="ডিলিট বিজনেস"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <button
            onClick={() => setIsAddBizModalOpen(true)}
            className="px-3 py-1.5 bg-blue-500/15 hover:bg-blue-500 text-blue-600 hover:text-white border border-blue-500/30 rounded-xl text-xs font-bold transition-all flex items-center gap-1 flex-shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>নতুন বিজনেস যোগ করুন</span>
          </button>
        </div>
      )}

      {/* SECTION: MY ACCOUNTS VIEW */}
      {activeSection === 'accounts' ? (
        <div className="space-y-6 animate-fade-in">
          {/* Header Net Worth Card */}
          <div className="p-6 bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-700 rounded-3xl text-white shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-teal-100 text-xs font-bold uppercase tracking-wider">
                <Coins className="w-4 h-4" />
                <span>সর্বমোট ব্যাংক ও ওয়ালেট ব্যালেন্স (Net Worth)</span>
              </div>
              <div className="text-3xl sm:text-4xl font-black font-mono mt-1 tracking-tight">
                ৳ {totalAccountsBalance.toLocaleString('en-US')}
              </div>
              <p className="text-xs text-teal-100/90 mt-1">
                আপনার সমস্ত ব্যাংক, বিকাশ, নগদ, ক্যাশ ও সঞ্চয়ী অ্যাকাউন্টের মোট স্থিতি।
              </p>
            </div>

            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                onClick={() => {
                  if (accounts.length >= 2) {
                    setTransferFromId(accounts[0].id);
                    setTransferToId(accounts[1].id);
                    setIsTransferModalOpen(true);
                  }
                }}
                disabled={accounts.length < 2}
                className="px-4 py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-md text-white rounded-2xl text-xs font-bold flex items-center gap-1.5 transition-all active:scale-95 disabled:opacity-50"
              >
                <ArrowLeftRight className="w-4 h-4" />
                <span>ফান্ড ট্রান্সফার</span>
              </button>

              <button
                onClick={handleOpenCreateAccount}
                className="px-4 py-2.5 bg-white text-teal-800 hover:bg-teal-50 rounded-2xl text-xs font-black shadow-md flex items-center gap-1.5 transition-all transform active:scale-95"
              >
                <Plus className="w-4 h-4" />
                <span>নতুন অ্যাকাউন্ট যোগ</span>
              </button>
            </div>
          </div>

          {/* Accounts Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {accounts.map((acc) => {
              return (
                <div
                  key={acc.id}
                  className="p-5 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs hover:shadow-md transition-all flex flex-col justify-between min-h-[160px] group"
                >
                  <div>
                    {/* Top Type Badge & Actions */}
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-10 h-10 rounded-2xl flex items-center justify-center p-2 shadow-xs"
                          style={{ backgroundColor: `${acc.color || '#10b981'}20` }}
                        >
                          {getAccountTypeIcon(acc.type)}
                        </div>
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 block uppercase tracking-wider">
                            {getAccountTypeLabel(acc.type)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleOpenEditAccount(acc)}
                          className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
                          title="এডিট অ্যাকাউন্ট"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        {accounts.length > 1 && (
                          <button
                            onClick={() => deleteAccount(acc.id)}
                            className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl"
                            title="মুছুন"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Account Name */}
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-white truncate">
                      {acc.name}
                    </h3>
                  </div>

                  {/* Balance & Adjust Button */}
                  <div className="pt-3 border-t border-slate-100 dark:border-slate-800/80 mt-4 flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 block">বর্তমান ব্যালেন্স</span>
                      <span className="text-lg font-black font-mono text-emerald-600 dark:text-emerald-400">
                        ৳ {Number(acc.balance || 0).toLocaleString('en-US')}
                      </span>
                    </div>

                    <button
                      onClick={() => {
                        setAdjustingAccount(acc);
                        setAdjustBalanceVal(String(acc.balance || 0));
                      }}
                      className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 hover:bg-teal-500/10 hover:text-teal-600 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-bold transition-colors"
                      title="ব্যালেন্স আপডেট করুন"
                    >
                      আপডেট
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : activeSection === 'loan' ? (
        <LoanManager />
      ) : (
        /* SPREADSHEET TEMPLATE CONTAINER (For Personal, Organization, Business) */
        <div className="bg-white dark:bg-[#111827] border-2 border-emerald-600/40 rounded-3xl shadow-md overflow-hidden print:border-none print:shadow-none w-full max-w-full min-w-0">
          {/* Template Green Banner Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white px-4 sm:px-6 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-2 shadow-inner">
            <h2 className="text-lg sm:text-xl font-black tracking-wide font-bangla text-center sm:text-left flex items-center gap-2">
              <span>{sectionTitle}</span>
            </h2>
            <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-end">
              <button
                onClick={() => setIsAddCategoryModalOpen(true)}
                className="px-3 py-1 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-xl text-xs font-black tracking-wide flex items-center gap-1.5 transition-colors"
                title="নতুন খাত যোগ করুন"
              >
                <Tag className="w-3.5 h-3.5" />
                <span>+ নতুন খাত</span>
              </button>
              <div className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-xl text-xs font-black tracking-wider">
                {monthLabel}
              </div>
            </div>
          </div>

          {/* Main Grid: Left = Expense Table, Right = Summary & Incomes */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-0 divide-y xl:divide-y-0 xl:divide-x divide-slate-200 dark:divide-slate-800 w-full max-w-full min-w-0">
            
            {/* LEFT: EXPENSE ENTRY TABLE (7 Cols on XL) */}
            <div className="xl:col-span-7 p-3 sm:p-5 space-y-4 w-full max-w-full min-w-0">
              
              {/* Inline Quick Add Row */}
              <form
                onSubmit={handleAddExpense}
                className="p-3 bg-emerald-50/70 dark:bg-emerald-950/20 border border-emerald-500/30 rounded-2xl flex flex-wrap items-center gap-2 shadow-xs"
              >
                {/* Day */}
                <div className="w-16">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase">তারিখ</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formDay}
                    onChange={(e) => setFormDay(Number(e.target.value))}
                    required
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white text-center focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>

                {/* Category */}
                <div className="w-36 sm:w-40">
                  <div className="flex items-center justify-between">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase">খাত</label>
                    <button
                      type="button"
                      onClick={() => setIsAddCategoryModalOpen(true)}
                      className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline"
                    >
                      + নতুন
                    </button>
                  </div>
                  <select
                    value={formCategory}
                    onChange={(e) => {
                      if (e.target.value === '__NEW__') {
                        setIsAddCategoryModalOpen(true);
                      } else {
                        setFormCategory(e.target.value);
                      }
                    }}
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  >
                    {availableCategories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                    <option value="__NEW__" className="text-emerald-600 font-bold">
                      + নতুন খাত যোগ করুন...
                    </option>
                  </select>
                </div>

                {/* Description */}
                <div className="flex-1 min-w-[130px]">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase">বিবরণ</label>
                  <input
                    type="text"
                    placeholder="বিবরণ লিখুন..."
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    className="w-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>

                {/* Amount */}
                <div className="w-24">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase">টাকা</label>
                  <input
                    type="number"
                    placeholder="টাকা"
                    value={formAmount}
                    onChange={(e) => setFormAmount(e.target.value)}
                    required
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white text-right focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-3.5">
                  <button
                    type="submit"
                    className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-sm flex items-center gap-1 transition-all active:scale-95"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>যোগ</span>
                  </button>
                </div>
              </form>

              {/* Expense Table */}
              <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-x-auto shadow-xs w-full max-w-full">
                <table className="w-full border-collapse text-left text-xs min-w-[460px]">
                  <thead>
                    <tr className="bg-slate-100 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-700 font-bangla font-black">
                      <th className="py-2.5 px-3 w-16 text-center border-r border-slate-200 dark:border-slate-700">তারিখ</th>
                      <th className="py-2.5 px-3 w-28 sm:w-32 border-r border-slate-200 dark:border-slate-700">খাত</th>
                      <th className="py-2.5 px-3 border-r border-slate-200 dark:border-slate-700">বিবরণ</th>
                      <th className="py-2.5 px-3 w-24 text-right border-r border-slate-200 dark:border-slate-700 text-rose-600 dark:text-rose-400 font-bold">
                        টাকা ({totalExpense})
                      </th>
                      <th className="py-2.5 px-2 w-10 text-center print:hidden"></th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
                    {monthExpenses.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-slate-400 text-xs">
                          এই মাসে কোনো খরচের এন্ট্রি নেই। উপরের বারে তথ্য লিখে &ldquo;যোগ&rdquo; করুন।
                        </td>
                      </tr>
                    ) : (
                      monthExpenses.map((exp, idx) => (
                        <tr
                          key={exp.id}
                          className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors ${
                            idx % 2 === 0 ? 'bg-white dark:bg-[#111827]' : 'bg-slate-50/50 dark:bg-slate-900/30'
                          }`}
                        >
                          <td className="py-2 px-3 text-center border-r border-slate-100 dark:border-slate-800 font-bold text-slate-800 dark:text-slate-200">
                            {exp.day}
                          </td>
                          <td className="py-2 px-3 border-r border-slate-100 dark:border-slate-800 font-semibold text-slate-700 dark:text-slate-300">
                            <span className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[11px]">
                              {exp.category}
                            </span>
                          </td>
                          <td className="py-2 px-3 border-r border-slate-100 dark:border-slate-800 text-slate-900 dark:text-slate-100 break-words font-bangla">
                            {exp.description}
                          </td>
                          <td className="py-2 px-3 text-right border-r border-slate-100 dark:border-slate-800 font-bold font-mono text-slate-900 dark:text-white">
                            {exp.amount}
                          </td>
                          <td className="py-2 px-1 text-center print:hidden">
                            <button
                              onClick={() => handleDeleteExpense(exp.id)}
                              className="p-1 text-slate-400 hover:text-rose-500 rounded-md transition-colors"
                              title="মুছুন"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>

                  {/* Table Footer: Total */}
                  <tfoot>
                    <tr className="bg-slate-100 dark:bg-slate-800 border-t-2 border-slate-300 dark:border-slate-700 font-black">
                      <td colSpan={3} className="py-2.5 px-4 text-right border-r border-slate-200 dark:border-slate-700 font-bangla">
                        সর্বমোট ব্যয়:
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-sm text-rose-600 dark:text-rose-400 font-black border-r border-slate-200 dark:border-slate-700">
                        {totalExpense}
                      </td>
                      <td className="print:hidden"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* RIGHT: SUMMARIES & RECEIPTS (5 Cols on XL) */}
            <div className="xl:col-span-5 p-3 sm:p-5 space-y-4 bg-slate-50/40 dark:bg-slate-900/20 w-full max-w-full min-w-0">
              
              {/* Box 1: একনজরে ব্যয় (Category Breakdown) */}
              <div className="border border-emerald-500/30 rounded-2xl overflow-hidden bg-white dark:bg-[#111827] shadow-xs">
                <div className="bg-emerald-600 text-white px-4 py-2 font-bangla font-black text-xs flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>একনজরে ব্যয়</span>
                    <button
                      onClick={() => setIsAddCategoryModalOpen(true)}
                      className="p-0.5 hover:bg-white/20 rounded text-[10px] font-normal flex items-center gap-0.5"
                      title="খাত যোগ করুন"
                    >
                      <Plus className="w-3 h-3" />
                      <span>খাত</span>
                    </button>
                  </div>
                  <span className="font-mono bg-white/20 px-2 py-0.5 rounded text-[11px] font-bold">
                    {totalExpense}
                  </span>
                </div>

                <div className="p-3">
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {availableCategories.map((cat) => {
                        const amount = categoryBreakdown[cat] || 0;
                        return (
                          <tr key={cat} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 group">
                            <td className="py-1.5 px-2 font-semibold text-slate-700 dark:text-slate-300 font-bangla flex items-center justify-between">
                              <span>{cat}</span>
                              {/* Delete custom category option */}
                              {availableCategories.length > 3 && (
                                <button
                                  onClick={(e) => handleDeleteCategory(cat, e)}
                                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 transition-opacity p-0.5 rounded"
                                  title="খাত মুছে ফেলুন"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono font-bold ${
                                amount > 0 ? 'text-slate-900 dark:text-white' : 'text-slate-400'
                              }`}
                            >
                              {amount}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-200 dark:border-slate-700 font-black text-xs">
                        <td className="py-2 px-2 font-bangla text-slate-900 dark:text-white">মোট ব্যয়</td>
                        <td className="py-2 px-2 text-right font-mono text-rose-600 dark:text-rose-400 font-black">
                          {totalExpense}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Box 2: একনজরে আয় (Income Receipts) */}
              <div className="border border-emerald-500/30 rounded-2xl overflow-hidden bg-white dark:bg-[#111827] shadow-xs">
                <div className="bg-emerald-600 text-white px-4 py-2 font-bangla font-black text-xs flex items-center justify-between">
                  <span>একনজরে আয়</span>
                  <span className="font-mono bg-white/20 px-2 py-0.5 rounded text-[11px] font-bold">
                    {totalIncome}
                  </span>
                </div>

                <div className="p-3 space-y-2.5">
                  {/* Inline Quick Add Income */}
                  <form onSubmit={handleAddIncome} className="flex items-center gap-2 w-full min-w-0">
                    <input
                      type="text"
                      placeholder="নাম / আয়ের উৎস..."
                      value={quickIncomeName}
                      onChange={(e) => setQuickIncomeName(e.target.value)}
                      className="flex-1 min-w-0 px-2.5 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                    <input
                      type="number"
                      placeholder="টাকা"
                      value={quickIncomeAmount}
                      onChange={(e) => setQuickIncomeAmount(e.target.value)}
                      className="w-16 sm:w-20 flex-shrink-0 px-2 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white text-right focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                    <button
                      type="submit"
                      className="p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-xs flex-shrink-0"
                      title="আয় যোগ করুন"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </form>

                  {/* Income List */}
                  <div className="max-h-48 overflow-y-auto pr-1">
                    <table className="w-full text-xs">
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {monthIncomes.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="text-center py-3 text-slate-400 text-[11px]">
                              কোনো আয়ের এন্ট্রি নেই।
                            </td>
                          </tr>
                        ) : (
                          monthIncomes.map((inc) => (
                            <tr key={inc.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                              <td className="py-1 px-2 font-semibold text-slate-700 dark:text-slate-300 font-bangla">
                                {inc.sourceName}
                              </td>
                              <td className="py-1 px-2 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                                {inc.amount}
                              </td>
                              <td className="py-1 pl-1 w-6 text-center print:hidden">
                                <button
                                  onClick={() => handleDeleteIncome(inc.id)}
                                  className="text-slate-400 hover:text-rose-500"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Box 3: আয় হতে দিয়েছি (Disbursements) */}
              <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden bg-white dark:bg-[#111827] shadow-xs">
                <div className="bg-slate-700 text-white px-4 py-2 font-bangla font-black text-xs flex items-center justify-between">
                  <span>আয় হতে দিয়েছি</span>
                  <span className="font-mono bg-white/20 px-2 py-0.5 rounded text-[11px] font-bold">
                    {totalDisbursement}
                  </span>
                </div>

                <div className="p-3 space-y-2">
                  {/* Inline Quick Add Disbursement */}
                  <form onSubmit={handleAddDisbursement} className="flex items-center gap-2 w-full min-w-0">
                    <input
                      type="text"
                      placeholder="গ্রহীতার নাম..."
                      value={quickDisbName}
                      onChange={(e) => setQuickDisbName(e.target.value)}
                      className="flex-1 min-w-0 px-2.5 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none"
                    />
                    <input
                      type="number"
                      placeholder="টাকা"
                      value={quickDisbAmount}
                      onChange={(e) => setQuickDisbAmount(e.target.value)}
                      className="w-16 sm:w-20 flex-shrink-0 px-2 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white text-right focus:outline-none"
                    />
                    <button
                      type="submit"
                      className="p-1.5 bg-slate-700 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex-shrink-0"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </form>

                  {/* Disbursed List */}
                  <div className="max-h-28 overflow-y-auto pr-1">
                    <table className="w-full text-xs">
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {monthDisbursements.map((disb) => (
                          <tr key={disb.id}>
                            <td className="py-1 px-2 font-medium text-slate-700 dark:text-slate-300 font-bangla">
                              {disb.recipientName}
                            </td>
                            <td className="py-1 px-2 text-right font-mono font-bold text-slate-800 dark:text-slate-200">
                              {disb.amount}
                            </td>
                            <td className="py-1 pl-1 w-6 text-center print:hidden">
                              <button
                                onClick={() => handleDeleteDisbursement(disb.id)}
                                className="text-slate-400 hover:text-rose-500"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Box 4: একনজরে (Final Summary & Current Balance) */}
              <div className="border-2 border-emerald-600 rounded-2xl overflow-hidden bg-white dark:bg-[#111827] shadow-sm">
                <div className="bg-emerald-600 text-white px-4 py-2 font-bangla font-black text-xs text-center">
                  একনজরে
                </div>

                <div className="p-3">
                  <table className="w-full text-xs font-bold">
                    <tbody>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-2 px-2 text-slate-600 dark:text-slate-400 font-bangla">মোট ব্যয়</td>
                        <td className="py-2 px-2 text-right font-mono text-rose-600 dark:text-rose-400 font-black">
                          {totalExpense}
                        </td>
                      </tr>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-2 px-2 text-slate-600 dark:text-slate-400 font-bangla">মোট আয়</td>
                        <td className="py-2 px-2 text-right font-mono text-emerald-600 dark:text-emerald-400 font-black">
                          {totalIncome}
                        </td>
                      </tr>
                      {totalDisbursement > 0 && (
                        <tr className="border-b border-slate-100 dark:border-slate-800">
                          <td className="py-2 px-2 text-slate-600 dark:text-slate-400 font-bangla">মোট প্রদান</td>
                          <td className="py-2 px-2 text-right font-mono text-slate-700 dark:text-slate-300 font-black">
                            {totalDisbursement}
                          </td>
                        </tr>
                      )}
                      <tr className="bg-emerald-50 dark:bg-emerald-950/40">
                        <td className="py-2.5 px-2 text-slate-900 dark:text-white font-bangla font-black text-sm">
                          বর্তমানে (হাতে আছে):
                        </td>
                        <td
                          className={`py-2.5 px-2 text-right font-mono font-black text-base ${
                            currentBalance >= 0
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-rose-600'
                          }`}
                        >
                          {currentBalance}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Category Modal */}
      {isAddCategoryModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsAddCategoryModalOpen(false);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-md bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                  <Tag className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">নতুন খাত যোগ করুন</h3>
              </div>
              <button
                onClick={() => setIsAddCategoryModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddCategory} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                  খাতের নাম
                </label>
                <input
                  type="text"
                  placeholder="যেমন: প্রচার ও প্রকাশনা, ইন্টারনেট বিল, ইত্যাদি"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsAddCategoryModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
                >
                  বাতিল
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/25 flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>খাত যুক্ত করুন</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Account Modal */}
      {isAddAccountModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsAddAccountModalOpen(false);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-md bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-teal-500/10 text-teal-500 flex items-center justify-center">
                  <WalletCards className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  {editingAccount ? 'অ্যাকাউন্ট এডিট করুন' : 'নতুন অ্যাকাউন্ট তৈরি করুন'}
                </h3>
              </div>
              <button
                onClick={() => setIsAddAccountModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveAccount} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                  অ্যাকাউন্টের নাম
                </label>
                <input
                  type="text"
                  placeholder="যেমন: বিকাশ পার্সোনাল, সিটি ব্যাংক, নগদ ওয়ালেট"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                  অ্যাকাউন্টের ধরন
                </label>
                <select
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value as AccountType)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="mobile_banking">মোবাইল ব্যাংকিং (bKash / Nagad / Rocket)</option>
                  <option value="bank">ব্যাংক একাউন্ট (Bank Account)</option>
                  <option value="cash">ক্যাশ ওয়ালেট (Cash in Hand)</option>
                  <option value="savings">সঞ্চয় ও ডিপিএস (Savings / DPS)</option>
                  <option value="credit_card">ক্রেডিট কার্ড (Credit Card)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                  বর্তমান ব্যালেন্স (টাকা)
                </label>
                <input
                  type="number"
                  placeholder="০"
                  value={accountBalance}
                  onChange={(e) => setAccountBalance(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsAddAccountModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
                >
                  বাতিল
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold shadow-md shadow-teal-500/25 flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>{editingAccount ? 'আপডেট করুন' : 'অ্যাকাউন্ট সেভ করুন'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fund Transfer Modal */}
      {isTransferModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsTransferModalOpen(false);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-md bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                  <ArrowLeftRight className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">ফান্ড ট্রান্সফার</h3>
              </div>
              <button
                onClick={() => setIsTransferModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleExecuteTransfer} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                  যে অ্যাকাউন্ট থেকে কাটবে (উৎস)
                </label>
                <select
                  value={transferFromId}
                  onChange={(e) => setTransferFromId(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:outline-none"
                >
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} (৳ {a.balance})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                  যে অ্যাকাউন্টে জমা হবে (গন্তব্য)
                </label>
                <select
                  value={transferToId}
                  onChange={(e) => setTransferToId(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:outline-none"
                >
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id} disabled={a.id === transferFromId}>
                      {a.name} (৳ {a.balance})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                  ট্রান্সফার টাকা (Amount)
                </label>
                <input
                  type="number"
                  placeholder="টাকার পরিমাণ লিখুন"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  required
                  min="1"
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsTransferModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
                >
                  বাতিল
                </button>
                <button
                  type="submit"
                  disabled={!transferAmount || Number(transferAmount) <= 0 || transferFromId === transferToId}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/25 flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>ট্রান্সফার সম্পন্ন করুন</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Balance Adjustment Modal */}
      {adjustingAccount && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setAdjustingAccount(null);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-sm bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                ব্যালেন্স আপডেট ({adjustingAccount.name})
              </h3>
              <button
                onClick={() => setAdjustingAccount(null)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveQuickBalance} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">সঠিক ব্যালেন্স লিখুন</label>
                <input
                  type="number"
                  value={adjustBalanceVal}
                  onChange={(e) => setAdjustBalanceVal(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-base font-black text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono text-center"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setAdjustingAccount(null)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 rounded-xl"
                >
                  বাতিল
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold shadow-md shadow-teal-500/25 flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>সেভ করুন</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Business Modal */}
      {isAddBizModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsAddBizModalOpen(false);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-md bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                  <Briefcase className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">নতুন বিজনেস যোগ করুন</h3>
              </div>
              <button
                onClick={() => setIsAddBizModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateBusiness} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">বিজনেস / শপের নাম</label>
                <input
                  type="text"
                  placeholder="যেমন: My SaaS, Digital Agency, Retail Shop"
                  value={newBizName}
                  onChange={(e) => setNewBizName(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">বিবরণ (ঐচ্ছিক)</label>
                <input
                  type="text"
                  placeholder="যেমন: সফটওয়্যার প্রজেক্ট এবং ফ্রিল্যান্সিং"
                  value={newBizDesc}
                  onChange={(e) => setNewBizDesc(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsAddBizModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
                >
                  বাতিল
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/25 flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>বিজনেস তৈরি করুন</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
